import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, TrendingUp, ShieldCheck, BarChart2, Users, FileSpreadsheet} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/components/ui/use-toast';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { toast } = useToast();

  const [form, setForm] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await login(form.username, form.password);
      toast({ title: 'Hoş geldiniz!', description: 'Başarıyla giriş yapıldı.', variant: 'success' });
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const msg = err.response?.data?.error || 'Giriş yapılırken bir hata oluştu';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const features = [
    {
      icon: <TrendingUp size={20} color="#60a5fa" />,
      bg: 'rgba(37,99,235,0.15)',
      title: 'Cari Hesap Takibi',
      desc: 'Alıcı ve satıcılarınızın bakiyelerini anlık olarak izleyin.',
    },
    {
      icon: <BarChart2 size={20} color="#a78bfa" />,
      bg: 'rgba(124,58,237,0.15)',
      title: 'Finansal Raporlar',
      desc: 'Aylık grafikler ve detaylı raporlarla finansal durumunuzu analiz edin.',
    },
    {
      icon: <FileSpreadsheet size={20} color="#34d399" />,
      bg: 'rgba(16,185,129,0.15)',
      title: 'Excel Dışa Aktarım',
      desc: 'Tüm verilerinizi tek tıkla Excel formatında indirin.',
    },
    {
      icon: <Users size={20} color="#fb923c" />,
      bg: 'rgba(234,88,12,0.15)',
      title: 'Çok Kullanıcılı Sistem',
      desc: 'Ekip üyelerinize özel izinler atayarak güvenli erişim sağlayın.',
    },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0f1e 0%, #0f172a 40%, #1a1040 70%, #0f172a 100%)',
      backgroundSize: '400% 400%',
      display: 'flex',
      alignItems: 'stretch',
      position: 'relative',
      overflow: 'hidden',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      animation: 'gradShift 12s ease infinite',
    }}>

      {/* ── LEFT PANEL ── */}
      <div style={{
        flex: '1 1 55%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        padding: '60px 56px',
        position: 'relative',
        overflow: 'hidden',
      }} className="login-left-panel">

        {/* Dot grid pattern */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'radial-gradient(rgba(99,102,241,0.15) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }} />

        {/* Animated glows */}
        <div style={{
          position: 'absolute', top: '-100px', left: '-100px',
          width: '500px', height: '500px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(37,99,235,0.28) 0%, transparent 65%)',
          pointerEvents: 'none', animation: 'pulseGlow 6s ease-in-out infinite',
        }} />
        <div style={{
          position: 'absolute', bottom: '-80px', right: '-80px',
          width: '420px', height: '420px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(124,58,237,0.22) 0%, transparent 65%)',
          pointerEvents: 'none', animation: 'pulseGlow 8s ease-in-out infinite reverse',
        }} />
        <div style={{
          position: 'absolute', top: '50%', right: '15%',
          width: '200px', height: '200px', borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(16,185,129,0.1) 0%, transparent 70%)',
          pointerEvents: 'none', animation: 'pulseGlow 5s ease-in-out infinite 2s',
        }} />

        {/* Brand */}
        <div style={{ marginBottom: '48px', position: 'relative', zIndex: 1, animation: 'fadeSlideUp 0.7s cubic-bezier(0.22,1,0.36,1) both' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
            {/* Logo with float + ring */}
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{
                position: 'absolute', width: '64px', height: '64px', borderRadius: '50%',
                border: '1px solid rgba(37,99,235,0.4)',
                animation: 'pingRing 2.5s ease-out infinite',
              }} />
              <div style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: '50px', height: '50px', borderRadius: '14px',
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                boxShadow: '0 8px 28px rgba(37,99,235,0.5)',
                animation: 'floatIcon 4s ease-in-out infinite',
                position: 'relative', zIndex: 1,
              }}>
                <TrendingUp size={24} color="white" />
              </div>
            </div>
            <div>
              <div style={{ fontSize: '26px', fontWeight: 800, letterSpacing: '0.18em', color: '#f1f5f9', lineHeight: 1 }}>CARIO</div>
              <div style={{ fontSize: '10px', color: 'rgba(148,163,184,0.7)', letterSpacing: '0.12em', marginTop: '3px', textTransform: 'uppercase' }}>Finans Yönetim Sistemi</div>
            </div>
          </div>

          <h2 style={{ fontSize: '30px', fontWeight: 800, color: '#f1f5f9', margin: '0 0 12px 0', lineHeight: 1.25 }}>
            İşletmenizin finansını<br />
            <span style={{
              background: 'linear-gradient(90deg, #60a5fa, #a78bfa, #34d399, #60a5fa)',
              backgroundSize: '300% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              animation: 'shimmerGrad 4s linear infinite',
            }}>
              tam kontrol altında
            </span>{' '}tutun.
          </h2>
          <p style={{ fontSize: '15px', color: 'rgba(148,163,184,0.8)', margin: 0, lineHeight: 1.7 }}>
            Cari hesaplar, işlemler, raporlar ve daha fazlası —<br />hepsi tek platformda.
          </p>
        </div>

        {/* Feature cards */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', position: 'relative', zIndex: 1 }}>
          {features.map((f, i) => (
            <div key={i} style={{
              backgroundColor: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: '14px',
              padding: '20px 16px',
              backdropFilter: 'blur(12px)',
              transition: 'border-color 0.25s, background-color 0.25s, transform 0.25s, box-shadow 0.25s',
              animation: `fadeSlideUp 0.6s cubic-bezier(0.22,1,0.36,1) ${0.3 + i * 0.1}s both`,
              cursor: 'default',
            }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.18)';
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 12px 28px rgba(0,0,0,0.3)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.04)';
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{
                width: '40px', height: '40px', borderRadius: '10px',
                backgroundColor: f.bg,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: '12px',
                transition: 'transform 0.2s',
              }}>
                {f.icon}
              </div>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#e2e8f0', marginBottom: '5px' }}>{f.title}</div>
              <div style={{ fontSize: '12px', color: 'rgba(148,163,184,0.7)', lineHeight: 1.55 }}>{f.desc}</div>
            </div>
          ))}
        </div>

        {/* Bottom badge */}
        <div style={{
          marginTop: '36px', display: 'flex', alignItems: 'center', gap: '8px',
          position: 'relative', zIndex: 1,
          animation: 'fadeSlideUp 0.6s cubic-bezier(0.22,1,0.36,1) 0.75s both',
        }}>
          <div style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#34d399', animation: 'blink 2s ease-in-out infinite' }} />
          <span style={{ fontSize: '12px', color: 'rgba(148,163,184,0.65)' }}>Türkiye'nin modern cari yönetim çözümü</span>
        </div>
      </div>

      {/* ── RIGHT PANEL ── */}
      <div style={{
        flex: '0 0 430px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 24px',
        position: 'relative',
        zIndex: 10,
      }} className="login-right-panel">
        <div style={{
          width: '100%',
          maxWidth: '370px',
          backgroundColor: 'rgba(255,255,255,0.05)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: '20px',
          boxShadow: '0 32px 64px -16px rgba(0,0,0,0.5), 0 0 0 1px rgba(37,99,235,0.15)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          padding: '44px 40px',
          animation: 'fadeSlideInRight 0.6s cubic-bezier(0.22,1,0.36,1) 0.1s both',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Card top accent line */}
          <div style={{
            position: 'absolute', top: 0, left: '20%', right: '20%', height: '2px',
            background: 'linear-gradient(90deg, transparent, #3b82f6, #8b5cf6, transparent)',
            borderRadius: '0 0 4px 4px',
          }} />

          {/* Logo mark on card */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '32px' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '10px',
              background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(37,99,235,0.4)',
            }}>
              <TrendingUp size={18} color="white" />
            </div>
            <div style={{ fontSize: '15px', fontWeight: 700, letterSpacing: '0.12em', color: '#f1f5f9' }}>CARIO</div>
          </div>

          {/* Heading */}
          <div style={{ marginBottom: '28px', animation: 'fadeSlideUp 0.5s ease 0.3s both' }}>
            <h1 style={{ fontSize: '22px', fontWeight: 700, color: '#f1f5f9', margin: '0 0 6px 0' }}>Hoş Geldiniz</h1>
            <p style={{ fontSize: '13px', color: 'rgba(148,163,184,0.8)', margin: 0 }}>Hesabınıza güvenle giriş yapın</p>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              backgroundColor: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.35)',
              borderRadius: '10px',
              padding: '10px 14px',
              marginBottom: '18px',
              fontSize: '13px',
              color: '#fca5a5',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              animation: 'fadeSlideUp 0.3s ease both',
            }}>
              <span>⚠</span> {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ animation: 'fadeSlideUp 0.5s ease 0.4s both' }}>
            {/* Username */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'rgba(148,163,184,0.9)', marginBottom: '7px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Kullanıcı Adı
              </label>
              <input
                type="text"
                placeholder="Kullanıcı Adı"
                value={form.username}
                onChange={(e) => setForm(f => ({ ...f, username: e.target.value }))}
                autoComplete="username"
                required
                style={{
                  width: '100%', height: '44px',
                  backgroundColor: 'rgba(255,255,255,0.07)',
                  border: '1px solid rgba(255,255,255,0.12)',
                  borderRadius: '10px',
                  padding: '0 14px',
                  fontSize: '14px',
                  color: '#f1f5f9',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s, box-shadow 0.2s, background-color 0.2s',
                }}
                onFocus={(e) => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.18)'; e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'; }}
                onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; e.target.style.boxShadow = 'none'; e.target.style.backgroundColor = 'rgba(255,255,255,0.07)'; }}
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: '28px' }}>
              <label style={{ display: 'block', fontSize: '11px', fontWeight: 600, color: 'rgba(148,163,184,0.9)', marginBottom: '7px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Şifre
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={form.password}
                  onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                  autoComplete="current-password"
                  required
                  style={{
                    width: '100%', height: '44px',
                    backgroundColor: 'rgba(255,255,255,0.07)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '10px',
                    padding: '0 44px 0 14px',
                    fontSize: '14px',
                    color: '#f1f5f9',
                    outline: 'none',
                    boxSizing: 'border-box',
                    transition: 'border-color 0.2s, box-shadow 0.2s, background-color 0.2s',
                  }}
                  onFocus={(e) => { e.target.style.borderColor = '#3b82f6'; e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.18)'; e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'rgba(255,255,255,0.12)'; e.target.style.boxShadow = 'none'; e.target.style.backgroundColor = 'rgba(255,255,255,0.07)'; }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  style={{
                    position: 'absolute', right: '12px', top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'rgba(148,163,184,0.7)',
                    display: 'flex', alignItems: 'center', padding: 0,
                    transition: 'color 0.2s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.color = '#60a5fa'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(148,163,184,0.7)'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%', height: '46px',
                background: isLoading
                  ? 'rgba(30,64,175,0.6)'
                  : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 50%, #4f46e5 100%)',
                backgroundSize: '200% auto',
                border: 'none', borderRadius: '10px',
                fontSize: '14px', fontWeight: 700,
                color: 'white',
                cursor: isLoading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                boxShadow: isLoading ? 'none' : '0 6px 24px rgba(37,99,235,0.5)',
                transition: 'all 0.25s',
                opacity: isLoading ? 0.75 : 1,
                animation: isLoading ? 'none' : 'btnShimmer 3s linear infinite',
              }}
              onMouseEnter={e => { if (!isLoading) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(37,99,235,0.6)'; }}}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = isLoading ? 'none' : '0 6px 24px rgba(37,99,235,0.5)'; }}
            >
              {isLoading ? (
                <>
                  <Loader2 size={16} style={{ animation: 'spin 0.8s linear infinite' }} />
                  Giriş yapılıyor...
                </>
              ) : (
                'Giriş Yap →'
              )}
            </button>
          </form>

          {/* Footer */}
          <div style={{
            marginTop: '28px', paddingTop: '20px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
            animation: 'fadeSlideUp 0.5s ease 0.6s both',
          }}>
            <ShieldCheck size={12} color="rgba(100,116,139,0.7)" />
            <p style={{ fontSize: '11px', color: 'rgba(100,116,139,0.7)', margin: 0 }}>
              Cario &copy; {new Date().getFullYear()} — Güvenli Giriş
            </p>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(24px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeSlideInRight {
          from { opacity: 0; transform: translateX(32px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        @keyframes pulseGlow {
          0%, 100% { opacity: 0.7; transform: scale(1); }
          50%       { opacity: 1;   transform: scale(1.08); }
        }
        @keyframes floatIcon {
          0%, 100% { transform: translateY(0px); }
          50%       { transform: translateY(-6px); }
        }
        @keyframes pingRing {
          0%   { transform: scale(0.9); opacity: 0.8; }
          70%  { transform: scale(1.6); opacity: 0; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        @keyframes shimmerGrad {
          0%   { background-position: 0% center; }
          100% { background-position: 300% center; }
        }
        @keyframes blink {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.3; }
        }
        @keyframes gradShift {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes btnShimmer {
          0%   { background-position: 200% center; }
          100% { background-position: -200% center; }
        }

        input::placeholder { color: var(--login-text-secondary); opacity: 0.6; }

        @media (max-width: 768px) {
          .login-left-panel { display: none !important; }
          .login-right-panel { flex: 1 1 100% !important; padding: 24px 16px !important; }
        }
      `}</style>
    </div>
  );
}
